import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { MedDataMock, MedicalIncluded } from '../types/history.type'
import { MedResData } from '../types/medical.type'
import { FamilyProps } from './useFamily'

export const useMedical = (arg?: FamilyProps) => {
  const patientId = arg?.patientId
  const id = arg?.id
  const sortDir = arg?.sortDir || 'desc'
  const query = arg?.query
  const limit = arg?.limit
  const page = arg?.page
  const intakeId = arg?.intakeId

  const { data: medicals, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/medical_histories?`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (limit) endpoint += `limit=${limit}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (query) endpoint += `query=${query}&`
      if (intakeId) endpoint += `intake_id=${intakeId}&`
      if (patientId) endpoint += `patient_id=${patientId}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedDataMock[], MedicalIncluded[]>>(endpoint)
      return data
    },
    queryKey: ['medical', { intakeId, limit, page, patientId, query, sortDir }]
  })

  const { data: medical } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedResData, MedicalIncluded[]>>(
        `/medical_histories/${id}`
      )
      return data
    },
    queryKey: ['medical', { id }]
  })

  const { mutateAsync: createMedical } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`/medical_histories`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] })
      queryClient.invalidateQueries({ queryKey: ['medical', { patientId }] })
    }
  })

  const { mutateAsync: updateMedical } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`medical_histories/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['medical'] })
      queryClient.invalidateQueries({ queryKey: ['medical', { id }] })
      queryClient.invalidateQueries({ queryKey: ['medical', { patientId }] })
    }
  })

  const { mutateAsync: updateMedicalStatus } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`medical_histories/${id}/update_status`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['medical'] })
      queryClient.invalidateQueries({ queryKey: ['medical', { id }] })
      queryClient.invalidateQueries({ queryKey: ['medical', { patientId }] })
    }
  })

  const { mutateAsync: deleteMedical } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`medical_histories/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['medical'] })
  })
  return {
    createMedical,
    deleteMedical,
    included: medicals?.included,
    isLoading,
    medical,
    medicals: medicals?.data,
    updateMedical,
    updateMedicalStatus
  }
}
